import React from 'react'
import { Settings, Home } from 'lucide-react'
import { useGameStore } from '../../store/gameStore'
import { useTheme } from '../../hooks/useTheme'

interface GameHeaderProps {
  onMenuClick?: () => void
  onHomeClick?: () => void
  onSettingsClick?: () => void
}

const GameHeader: React.FC<GameHeaderProps> = ({ onMenuClick, onHomeClick, onSettingsClick }) => {
  const { gameState } = useGameStore()
  const theme = useTheme()

  if (!gameState) return null

  const { round } = gameState
  const roundNumber = round.roundNumber
  const phaseType = round.phase.type

  // Format phase name for display
  const formatPhase = (phase: string) => {
    switch (phase) {
      case 'awaiting_card_play':
        return 'Play a Card'
      case 'auction':
        return 'Auction'
      case 'round_ending':
        return 'Round Ending'
      case 'selling_to_bank':
        return 'Selling Phase'
      case 'round_complete':
        return 'Round Complete'
      default:
        return phase.replace(/_/g, ' ')
    }
  }

  return (
    <header
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 24px',
        background: 'rgba(0, 0, 0, 0.4)',
        backdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${theme.border}`,
      }}
    >
      {/* Left: Logo/Menu */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <button
          onClick={onHomeClick}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
            background: theme.buttonSecondary,
            border: `1px solid ${theme.border}`,
            borderRadius: '8px',
            color: theme.text,
            cursor: 'pointer',
            transition: 'all 0.15s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = theme.buttonSecondaryHover
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = theme.buttonSecondary
          }}
        >
          <Home size={18} />
        </button>
        <span
          style={{
            fontSize: '18px',
            fontWeight: 300,
            color: theme.text,
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
          }}
        >
          Bougie Art
        </span>
      </div>

      {/* Center: Round & Phase */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '32px' }}>
        {/* Round indicator */}
        <div data-tutorial-id="round-indicator" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span
            style={{
              fontSize: '12px',
              color: theme.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
            }}
          >
            Round
          </span>
          <div style={{ display: 'flex', gap: '4px' }}>
            {[1, 2, 3, 4].map((r) => (
              <div
                key={r}
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '6px',
                  background:
                    r < roundNumber
                      ? theme.primaryMuted
                      : r === roundNumber
                      ? theme.primary
                      : theme.buttonSecondary,
                  border:
                    r === roundNumber
                      ? `2px solid ${theme.primary}`
                      : `1px solid ${theme.border}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: r === roundNumber ? 700 : 500,
                  color: r === roundNumber ? '#000' : theme.textSecondary,
                }}
              >
                {r}
              </div>
            ))}
          </div>
        </div>

        {/* Phase indicator */}
        <div
          style={{
            padding: '8px 20px',
            background: theme.phaseBadge,
            border: `1px solid ${theme.primaryMuted.replace('0.2', '0.3')}`,
            borderRadius: '20px',
            color: theme.roundIndicator,
            fontSize: '14px',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          {formatPhase(phaseType)}
        </div>
      </div>

      {/* Right: Settings */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <button
          onClick={onSettingsClick}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
            background: theme.buttonSecondary,
            border: `1px solid ${theme.border}`,
            borderRadius: '8px',
            color: theme.text,
            cursor: 'pointer',
            transition: 'all 0.15s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = theme.buttonSecondaryHover
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = theme.buttonSecondary
          }}
        >
          <Settings size={18} />
        </button>
      </div>
    </header>
  )
}

export default GameHeader
